import type { TOC } from '@ember/component/template-only'
import { eq } from 'ember-truth-helpers'
import { t } from 'ember-intl'
import { concat } from '@ember/helper'
import type Student from 'teacher-ui-client/models/student'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import gradeName from 'teacher-ui-client/helpers/grade-name'
import encodeURIComponent from 'teacher-ui-client/helpers/encode-uri-component'
import qrCode from 'teacher-ui-client/modifiers/qr-code'
import { LoginCardLogos } from 'teacher-ui-client/components/login-card/logos'
import { LoginCardFooter } from 'teacher-ui-client/components/login-card/footer'

interface Signature {
  Element: HTMLDivElement
  Args: {
    Named: {
      student: Student
      subscriptionType: SubscriptionType
    }
  }
}

// The font used here is Varela Round, a google font similar to Vag Rounded, which we use for student interfaces
// Disable prettier here, the removed whitespace for the outer div is important in ensuring no gaps between login cards
// prettier-ignore
export const LoginCard: TOC<Signature> = <template><div
  class="inline-grid grid-flow-col grid-cols-2 grid-rows-6 border-2 border-dusty-black-100 font-['Varela_Round'] text-xs"
  ...attributes
>
  {{! Left }}
  <div class="row-span-3 flex flex-col px-3 pb-3 pt-6 text-center">
    <LoginCardLogos @subscriptionType={{@subscriptionType}} />
    <div class="mt-auto wrap-break-word text-xl font-bold">{{@student.fullNameLastInitial}}</div>
    <div class="mb-auto">{{t "grade.label"}}: {{gradeName @student.gradePosition}}</div>
  </div>
  <dl
    class="row-span-2 self-center border-y p-3
      {{if (eq @subscriptionType SubscriptionType.Reading) 'border-oceany-blue-150'}}
      {{if (eq @subscriptionType SubscriptionType.Maths) 'border-ms-green-150'}}
      {{if (eq @subscriptionType SubscriptionType.Writing) 'border-wl-blue-150'}}
      "
  >
    <dt>{{t "manage.login"}}:</dt>
    <dd class="mb-2 wrap-break-word">{{@student.login}}</dd>
    <dt>{{t "manage.password"}}:</dt>
    <dd class="wrap-break-word">{{@student.plainPassword}}</dd>
  </dl>
  <div class="flex items-center justify-center self-stretch p-3 text-center">
    <LoginCardFooter @subscriptionType={{@subscriptionType}} />
  </div>
  {{! Right }}
  {{#let "border-l-2 border-dashed border-dusty-black-100" as |borderClass|}}
    {{! Qrcode moved away from border with larger top padding, to avoid it being cut by mistake }}
    <div class="row-span-3 px-3 pb-3 pt-6 text-center {{borderClass}}">
      {{#let
        (concat
          "https://"
          (encodeURIComponent @student.login)
          ":"
          (encodeURIComponent @student.plainPassword)
          "@sso.readingeggs.com/"
        )
        as |encode|
      }}
        <img
          data-test-card-qr-code={{encode}}
          class="aspect-square h-full min-h-[25mm] w-full min-w-[25mm] object-contain"
          alt={{t "components.loginCard.qrCodeAlt"}}
          {{qrCode encode=encode}}
        />
      {{/let}}
    </div>
    <div class="row-span-2 flex flex-col px-3 pt-3 text-center {{borderClass}}">
      <div data-test-login-card-name class="wrap-break-word text-base font-bold">{{@student.fullNameLastInitial}}</div>
      <div class="mb-auto">{{t "grade.label"}}: {{gradeName @student.gradePosition}}</div>
      <LoginCardLogos @subscriptionType={{@subscriptionType}} />
    </div>
    <div class="flex items-center justify-center self-stretch p-3 text-center {{borderClass}}">
      <LoginCardFooter @subscriptionType={{@subscriptionType}} />
    </div>
  {{/let}}
</div></template>

export default LoginCard
